import { Link } from 'react-router-dom';
import logo from '@/assets/damian-keyes-logo.png';

interface FooterProps {
  applyUrl?: string;
}

const companyLogos = [
  'Universal Music',
  'Sony Music',
  'Cinq Music',
  'Spotify',
  'Tidal',
  'BBC'
];

export function Footer({ applyUrl }: FooterProps) {
  return (
    <footer data-nav-theme="dark" className="border-t border-white/[0.03] py-12 lg:py-16" style={{ background: 'linear-gradient(to bottom, hsl(0, 0%, 3%) 0%, hsl(0, 0%, 2%) 100%)' }}>
      {/* Logo and Tagline at Top - Compact */}
      <div className="max-w-5xl mx-auto px-6 mb-6">
        <img 
          src={logo} 
          alt="Damian Keyes Logo" 
          className="w-32 lg:w-40 mb-2 -ml-1 invert"
        />
        <h3 className="text-sm lg:text-base font-medium text-white/70">
          Damian Keyes
        </h3>
        <p className="text-[10px] text-white/40 tracking-wide mt-1">
          Entrepreneur · BIMM Co-Founder
        </p>
      </div>

      {/* Two-Column Layout */}
      <div className="max-w-5xl mx-auto px-6 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left: Company Logos */}
          <div>
            <p className="text-[10px] text-white/40 uppercase tracking-wider mb-3 font-mono">
              We've worked with
            </p>
            <div className="grid grid-cols-2 gap-2">
              {companyLogos.map((name) => (
                <div
                  key={name}
                  className="text-[11px] text-white/60 hover:text-white/70 transition-colors duration-200"
                >
                  {name}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Navigation Links */}
          <div className="lg:text-right">
            <p className="text-[10px] text-white/40 uppercase tracking-wider mb-3 font-mono">
              Quick Links
            </p>
            <div className="flex flex-col gap-2">
              <a 
                href="https://www.dk-mba.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[11px] text-white/60 hover:text-white/70 transition-colors duration-200"
              >
                The Academy
              </a>
              <a 
                href="https://www.dk-artists.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[11px] text-white/60 hover:text-white/70 transition-colors duration-200"
              >
                The Agency
              </a>
              <a 
                href="mailto:hello@dk-mba.com"
                className="text-[11px] text-white/60 hover:text-white/70 transition-colors duration-200"
              >
                Email Us
              </a>
              <Link 
                to="/privacy-policy"
                className="text-[11px] text-white/60 hover:text-white/70 transition-colors duration-200"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="max-w-5xl mx-auto px-6 mt-6 pt-6 border-t border-white/10">
        {/* Social Icons - Centered above copyright */}
        <div className="flex items-center justify-center gap-4 mb-4">
          <a 
            href="https://www.youtube.com/user/Damiankeyes" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white/40 hover:text-[hsl(var(--accent-a))] transition-colors duration-300"
            aria-label="YouTube"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </a>
          
          <a 
            href="https://www.tiktok.com/@damiankeyes" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white/40 hover:text-[hsl(var(--accent-a))] transition-colors duration-300"
            aria-label="TikTok"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
            </svg>
          </a>
          
          <a 
            href="https://www.instagram.com/damiankeyes1/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white/40 hover:text-[hsl(var(--accent-a))] transition-colors duration-300"
            aria-label="Instagram"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          
          <a 
            href="mailto:hello@dk-mba.com"
            className="text-white/40 hover:text-[hsl(var(--accent-a))] transition-colors duration-300"
            aria-label="Email"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
          </a>
        </div>
        
        <p className="text-center text-[11px] text-white/40">
          © 2026 DK Music Business Academy Ltd
        </p>
      </div>
    </footer>
  );
}

export default Footer;
